/**
 * Contact queries and mutations.
 *
 * Each mutation seeds the detail cache from its own response and invalidates
 * the lists, the same rule the other entities follow: the response carries the
 * record's new version, and a refetch racing it could hand a form a version
 * that is already stale.
 *
 * Promoting a primary contact invalidates the DETAILS as well as the lists.
 * That is specific to this entity — the server demotes the previous primary in
 * the same transaction, so a record this client never touched has changed both
 * its flag and its version. Leaving it cached would show two primary contacts
 * for one startup and hand the stale one a version that no longer exists
 * (PRD §5.9).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.5
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  ContactDetail,
  CreateContactRequest,
  ListContactsQuery,
  UpdateContactRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as contactsApi from './contacts.api';
import type { ContactPage } from './contacts.api';

export function useContacts(query: ListContactsQuery): UseQueryResult<ContactPage> {
  return useQuery({
    queryKey: queryKeys.contacts.list(query),
    queryFn: () => contactsApi.listContacts(query),
  });
}

export function useContact(id: string): UseQueryResult<ContactDetail> {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id),
    queryFn: () => contactsApi.getContact(id),
  });
}

/**
 * Seeds the detail cache and invalidates what the write could have changed.
 *
 * `wasPromotion` widens the invalidation to every cached contact detail,
 * because a promotion silently demoted somebody else server-side.
 */
function useApplyContact(): (contact: ContactDetail, wasPromotion: boolean) => void {
  const queryClient = useQueryClient();

  return (contact: ContactDetail, wasPromotion: boolean) => {
    queryClient.setQueryData(queryKeys.contacts.detail(contact.id), contact);
    void queryClient.invalidateQueries({ queryKey: queryKeys.contacts.lists() });

    if (wasPromotion) {
      /* Everything except the record we just seeded, which is authoritative. */
      void queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.details(),
        predicate: (query) => query.queryKey.at(-1) !== contact.id,
      });
    }
  };
}

export function useCreateContact(): UseMutationResult<
  ContactDetail,
  Error,
  CreateContactRequest
> {
  const apply = useApplyContact();

  return useMutation({
    mutationFn: (body: CreateContactRequest) => contactsApi.createContact(body),
    onSuccess: (contact, body) => {
      apply(contact, body.isPrimary === true);
    },
  });
}

export function useUpdateContact(
  id: string,
): UseMutationResult<ContactDetail, Error, UpdateContactRequest> {
  const apply = useApplyContact();

  return useMutation({
    mutationFn: (body: UpdateContactRequest) => contactsApi.updateContact(id, body),
    onSuccess: (contact, body) => {
      apply(contact, body.isPrimary === true);
    },
  });
}
